/// <reference types="aframe" />

// @ts-ignore
const AFRAME = (window as any).AFRAME;
// @ts-ignore
const THREE = (window as any).THREE;

import type { Object3D, MathUtils } from 'three';

declare global {
  interface Window {
    AFRAME: typeof AFRAME;
    THREE: {
      Object3D: typeof Object3D;
      MathUtils: typeof MathUtils;
    };
  }
}

// Declare module interfaces for external libraries
export {};

declare module 'aframe' {
  const AFRAME: any;
}

declare module 'three' {
  const THREE: any;
}

// Register ensure-waves component
AFRAME.registerComponent('ensure-waves', {
  init: function() {
    this.initOcean = () => {
      const oceanEl = document.querySelector('#ocean-surface');
      if (oceanEl && (oceanEl as any).components['wobble-geometry']) {
        const wg = (oceanEl as any).components['wobble-geometry'];
        if (typeof wg.play === 'function') {
          wg.play();
        }
      }
    };
    
    // Set multiple attempts with increasing delays
    const delays = [500, 1000, 2000, 3000, 5000];
    delays.forEach(delay => {
      setTimeout(this.initOcean, delay);
    });
  }
});

// Register camera-rotation-initializer component
AFRAME.registerComponent('camera-rotation-initializer', {
  schema: {
    position: {type: 'vec3', default: {x: -16.9, y: 10.9, z: -20.4}},
    rotation: {type: 'vec3', default: {x: -7, y: 199, z: 0}},
  },
  
  init: function() {
    this.setInitialPose = this.setInitialPose.bind(this);
    setTimeout(this.setInitialPose, 100);
    setTimeout(this.setInitialPose, 500);
    setTimeout(this.setInitialPose, 1000);
  },
  
  setInitialPose: function() {
    const camera = document.querySelector('#custom-camera');
    if (camera) {
      const pos = this.data.position;
      const rot = this.data.rotation;
      
      camera.setAttribute('position', `${pos.x} ${pos.y} ${pos.z}`);
      camera.setAttribute('rotation', `${rot.x} ${rot.y} ${rot.z}`);
      
      if ((camera as any).object3D) {
        (camera as any).object3D.position.set(pos.x, pos.y, pos.z);
        (camera as any).object3D.rotation.set(
          THREE.MathUtils.degToRad(rot.x),
          THREE.MathUtils.degToRad(rot.y),
          THREE.MathUtils.degToRad(rot.z)
        );
      }
    }
  }
});

// Register controls-helper component
AFRAME.registerComponent('controls-helper', {
  schema: {
    minHeight: {type: 'number', default: 9.0},
    maxHeight: {type: 'number', default: 20.0},
    boundsX: {type: 'array', default: [-50, 50]},
    boundsZ: {type: 'array', default: [-100, 30]},
  },
  
  init: function() {
    this.keysPressed = new Set();
    this.speed = 0.15;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      this.keysPressed.add(e.code);
      if (['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyQ', 'KeyE'].includes(e.code)) {
        e.preventDefault();
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      this.keysPressed.delete(e.code);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    this.handleKeyDown = handleKeyDown;
    this.handleKeyUp = handleKeyUp;
  },
  
  moveCamera: function() {
    const camera = document.querySelector('#custom-camera');
    if (!camera || !(camera as any).object3D) return;
    
    const cameraObj = (camera as any).object3D;
    const currentPosition = cameraObj.position.clone();
    let newPosition = currentPosition.clone();
    
    // Forward/backward
    if (this.keysPressed.has('KeyW') || this.keysPressed.has('ArrowUp')) {
      newPosition.z -= this.speed * Math.cos(cameraObj.rotation.y);
      newPosition.x -= this.speed * Math.sin(cameraObj.rotation.y);
    }
    if (this.keysPressed.has('KeyS') || this.keysPressed.has('ArrowDown')) {
      newPosition.z += this.speed * Math.cos(cameraObj.rotation.y);
      newPosition.x += this.speed * Math.sin(cameraObj.rotation.y);
    }
    
    // Left/right
    if (this.keysPressed.has('KeyA') || this.keysPressed.has('ArrowLeft')) {
      newPosition.x -= this.speed * Math.cos(cameraObj.rotation.y);
      newPosition.z += this.speed * Math.sin(cameraObj.rotation.y);
    }
    if (this.keysPressed.has('KeyD') || this.keysPressed.has('ArrowRight')) {
      newPosition.x += this.speed * Math.cos(cameraObj.rotation.y);
      newPosition.z -= this.speed * Math.sin(cameraObj.rotation.y);
    }
    
    // Up/down
    if (this.keysPressed.has('KeyQ')) {
      newPosition.y += this.speed;
    }
    if (this.keysPressed.has('KeyE')) {
      newPosition.y -= this.speed;
    }
    
    // Apply boundaries
    newPosition.y = Math.max(this.data.minHeight, Math.min(this.data.maxHeight, newPosition.y));
    newPosition.x = Math.max(this.data.boundsX[0], Math.min(this.data.boundsX[1], newPosition.x));
    newPosition.z = Math.max(this.data.boundsZ[0], Math.min(this.data.boundsZ[1], newPosition.z));
    
    if (!newPosition.equals(currentPosition)) {
      cameraObj.position.copy(newPosition);
    }
  },
  
  tick: function() {
    this.moveCamera();
  },
  
  remove: function() {
    if (this.handleKeyDown) window.removeEventListener('keydown', this.handleKeyDown);
    if (this.handleKeyUp) window.removeEventListener('keyup', this.handleKeyUp);
  }
}); 